<script setup lang="ts">
import {
  GetStockReconciliationDetail,
  SubmitStockReconciliation,
  UpdateStockReconciliation,
} from "@/api/stockReconciliationApi";
import ConfirmActionDialog from "@/components/dialogs/ConfirmActionDialog.vue";
import StockReconciliationFormLayout from "@/components/warehouse/StockReconciliationFormLayout.vue";
import StockReconciliationItemsTable from "@/components/warehouse/StockReconciliationItemsTable.vue";
import { useStockReconciliationForm } from "@/composables/useStockReconciliationForm";
import { canDoctype } from "@/helper/permissionHelper";
import { presentStockReconciliationError } from "@/services/stockReconciliationErrors";
import useToastify from "@/stores/toastify.store";
import type { StockReconciliationDetail } from "@/type/stockReconciliation";

/** ⚠️ Inline literal — `definePage` meta is hoisted out of component scope. */
definePage({ meta: { permissionKey: "page.warehouse.stock_reconciliations" } });

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const toast = useToastify();

// Cast as the sibling stock-entry detail page does: the generated route types do
// not narrow `params` for a freshly added route until they are regenerated, and a
// direct `route.params.id` fails to typecheck against the union of every route.
const reconciliationId = computed(() => String((route.params as Record<string, unknown>)?.id ?? ""));

const loading = ref(false);
const saving = ref(false);
const submitting = ref(false);
const loadFailed = ref(false);
const inlineError = ref<{ title: string; message: string } | null>(null);
const submitDialogVisible = ref(false);

const reconciliation = ref<StockReconciliationDetail | null>(null);
const isEditing = ref(route.query.mode === "edit");

const recoForm = useStockReconciliationForm({ reconciliation, isEditing });

const canWrite = computed(() => canDoctype("Stock Reconciliation", "write"));

/**
 * ⚠️ Posting writes the stock ledger and cannot be undone, so it is gated on the
 * doctype's own `submit` permission. Being able to open the page, or to write to
 * the draft, never authorizes the operation.
 */
const canSubmit = computed(() => canDoctype("Stock Reconciliation", "submit"));

const isDraft = computed(() => recoForm.isDraft.value);
const canEdit = computed(() => isDraft.value && canWrite.value);

const canSave = computed(() =>
  canEdit.value
  && isEditing.value
  && !saving.value
  && Boolean(recoForm.form.postingDate)
  && recoForm.hasWarehouse()
  && recoForm.countedRows.value.length > 0,
);

/**
 * ⚠️ Says out loud that the backend keeps only differing lines.
 *
 * Without this, someone who counted forty products and found two discrepancies
 * reopens the document, sees two rows, and reasonably concludes the app threw
 * their work away.
 */
const droppedLinesNotice = computed(() =>
  !isDraft.value && reconciliation.value != null,
);

async function load() {
  if (!reconciliationId.value)
    return;

  loadFailed.value = false;

  const detail = await GetStockReconciliationDetail(loading, reconciliationId.value);

  // ⚠️ `null` is a FAILED read, not an empty document. Rendering a blank form over
  // it would invite someone to re-count a document that already exists.
  if (!detail) {
    loadFailed.value = true;

    return;
  }

  reconciliation.value = detail;
  recoForm.syncForm(detail);
}

onMounted(load);

async function save() {
  if (!canSave.value)
    return;

  if (!recoForm.hasChangedRows()) {
    toast.warning({ text: t("alkokh.stockReconciliation.messages.noChanges") });

    return;
  }

  inlineError.value = null;

  try {
    const updated = await UpdateStockReconciliation(
      saving,
      reconciliationId.value,
      recoForm.buildPayload(),
    );

    if (!updated)
      return;

    reconciliation.value = updated;
    recoForm.syncForm(updated);
    isEditing.value = false;
    toast.success({ text: t("alkokh.stockReconciliation.messages.updated") });
  } catch (error: any) {
    const presentation = presentStockReconciliationError(error, t);
    toast.error({ text: presentation.toastMessage });

    if (presentation.alertTitle && presentation.alertMessage) {
      inlineError.value = {
        title: presentation.alertTitle,
        message: presentation.alertMessage,
      };
    }
  }
}

async function submit() {
  inlineError.value = null;

  try {
    const submitted = await SubmitStockReconciliation(submitting, reconciliationId.value);
    if (!submitted)
      return;

    reconciliation.value = submitted;
    recoForm.syncForm(submitted);
    isEditing.value = false;
    submitDialogVisible.value = false;
    toast.success({ text: t("alkokh.stockReconciliation.messages.submitted") });
  } catch (error: any) {
    const presentation = presentStockReconciliationError(error, t);
    toast.error({ text: presentation.toastMessage });

    if (presentation.alertTitle && presentation.alertMessage) {
      inlineError.value = {
        title: presentation.alertTitle,
        message: presentation.alertMessage,
      };
    }
  }
}

function cancelEdit() {
  if (reconciliation.value)
    recoForm.syncForm(reconciliation.value);

  isEditing.value = false;
}
</script>

<template>
  <div>
    <!-- A failed read gets its own state. It is not the same as a document with
         no lines, and offering a blank form over it invites a duplicate count. -->
    <VAlert
      v-if="loadFailed"
      type="error"
      variant="tonal"
      :title="t('alkokh.stockReconciliation.notFound')"
      :text="t('alkokh.stockReconciliation.loadFailed')"
    >
      <template #append>
        <VBtn
          size="small"
          variant="text"
          :loading="loading"
          @click="load"
        >
          {{ t("alkokh.stockReconciliation.items.retry") }}
        </VBtn>
      </template>
    </VAlert>

    <StockReconciliationFormLayout
      v-else
      :form-state="recoForm"
      mode="detail"
      :is-editing="isEditing"
      :loading="loading || saving"
      :reconciliation-no="reconciliationId"
    >
      <template #actions>
        <VBtn
          variant="outlined"
          color="secondary"
          :disabled="saving || submitting"
          @click="router.push('/warehouse/stock-reconciliations')"
        >
          {{ t("alkokh.stockReconciliation.actions.back") }}
        </VBtn>

        <VBtn
          v-if="canEdit && !isEditing"
          variant="outlined"
          color="primary"
          prepend-icon="tabler-pencil"
          @click="isEditing = true"
        >
          {{ t("alkokh.stockReconciliation.editAction") }}
        </VBtn>

        <VBtn
          v-if="isEditing"
          variant="outlined"
          color="secondary"
          :disabled="saving"
          @click="cancelEdit"
        >
          {{ t("alkokh.global.cancel") }}
        </VBtn>

        <VBtn
          v-if="isEditing"
          color="primary"
          prepend-icon="tabler-device-floppy"
          :disabled="!canSave"
          :loading="saving"
          @click="save"
        >
          {{ t("alkokh.stockReconciliation.actions.save") }}
        </VBtn>

        <VBtn
          v-if="isDraft && !isEditing && canSubmit"
          color="success"
          prepend-icon="tabler-check"
          :loading="submitting"
          @click="submitDialogVisible = true"
        >
          {{ t("alkokh.stockReconciliation.actions.submit") }}
        </VBtn>
      </template>

      <template #alert>
        <VAlert
          v-if="inlineError"
          type="error"
          variant="tonal"
          :title="inlineError.title"
          :text="inlineError.message"
          closable
          @click:close="inlineError = null"
        />
      </template>

      <template #items>
        <StockReconciliationItemsTable
          :rows="recoForm.rows.value"
          :warehouse="recoForm.form.warehouse"
          :snapshot-state="recoForm.snapshotState.value"
          :loading="recoForm.snapshotLoading.value"
          :readonly="!isEditing"
          :opening-stock="recoForm.form.purpose === 'Opening Stock'"
          @update:rows="recoForm.updateRows"
          @retry-snapshot="recoForm.refreshSnapshot"
        />
      </template>

      <template #aside>
        <VAlert
          v-if="droppedLinesNotice"
          type="info"
          variant="tonal"
          density="compact"
          :text="t('alkokh.stockReconciliation.effect.pendingDesc')"
        />
      </template>

      <ConfirmActionDialog
        v-model:is-dialog-visible="submitDialogVisible"
        :title="t('alkokh.stockReconciliation.submitDialog.title')"
        :message="t('alkokh.stockReconciliation.submitDialog.message', { id: reconciliationId })"
        :confirm-btn="t('alkokh.stockReconciliation.submitDialog.confirm')"
        confirm-color="success"
        icon="tabler-check"
        :loading="submitting"
        @confirm="submit"
      />
    </StockReconciliationFormLayout>
  </div>
</template>
